import React from 'react';

import {
  HeadlineItemsFragment,
  useCreateHeadlineItemMutation,
  useDeleteHeadlineItemMutation,
  useHeadlineQuery,
  useUpdateHeadlineItemMutation,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemProps } from '../BasicItem';
import Form, { InitialValues, initialValues, validationSchema } from './Form';

export const HeadlineItem = ({ onSaveHandler }: PageItemProps) => {
  const { pageId, itemId, isNew, close } = useManageItem();

  const [update, { loading: updating }] = useUpdateHeadlineItemMutation();
  const [create, { loading: creating }] = useCreateHeadlineItemMutation();
  const [destroy, { loading: deleting }] = useDeleteHeadlineItemMutation();

  const { data, loading } = useHeadlineQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.headlineItem;

  const save = async (values: InitialValues): Promise<void> => {
    if (item?.id) {
      await update({
        variables: {
          attributes: {
            id: item.id,
            pageId: pageId,
            title: values.title || '',
            style: values.style || '',
          },
        },
        update: (_cache, { data }) => close(data?.updateHeadlineItem),
      });
    } else {
      await create({
        variables: {
          attributes: {
            pageId,
            title: values.title || '',
            style: values.style || '',
          },
        },
        update: (cache, { data }) => {
          addItemToPage<HeadlineItemsFragment | undefined>(cache, pageId, data?.createHeadlineItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  const onDelete = async () => {
    if (itemId) {
      await destroy({
        variables: {
          attributes: {
            id: itemId,
            pageId,
          },
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Title`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || creating || deleting}
      close={close}
      pageId={pageId}
      isNew={isNew}
      onDelete={onDelete}
    />
  );
};
